// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@flip/App/Components/Common/XngZone";
import { xHeaderUpdate } from "@flip/App/Handlers/XNG/Header";
import { useElementSize } from "@flip/App/Hooks/useSize";
import { CustomAppState } from "@flip/App/Store/Reducers";
import {
    getCartLinkWidget,
    getHeaderActionsWidget,
    getHeaderLinkListWidget,
    getHeaderSignInWidget,
    getLogoImageWidget,
    getMainNavigationWidget,
} from "@flip/App/Store/Selectors/GeneralSelectors";
import { FormInputWrapper } from "@flip/MobiusOverrides/FormField/FormField";
import { InputStyle } from "@flip/MobiusOverrides/TextField";
import { checkoutPages, constants } from "@flip/XDL/constants";
import MenuFocused from "@flip/XDL/Icons/MenuFocused";
import MenuInactive from "@flip/XDL/Icons/MenuInactive";
import { placeholderStyle } from "@flip/XDL/PreStyle/formField";
import { color, layoutContainer, xClassNames } from "@flip/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { Styles, StylesProvider } from "@insite/content-library/additionalStyles";
import Clickable from "@insite/mobius/Clickable";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import Menu from "@insite/mobius/Icons/Menu";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { breakpointMediaQueries, getProp, injectCss, resolveColor } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Transition } from "react-transition-group";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    return {
        logoImageWidget: getLogoImageWidget(state),
        mainNavigationWidget: getMainNavigationWidget(state),
        headerSignInWidget: getHeaderSignInWidget(state),
        cartLinkWidget: getCartLinkWidget(state),
        headerActionsWidget: getHeaderActionsWidget(state),
        headerLinkListWidget: getHeaderLinkListWidget(state),
        isTabletScreen: state.xCommon.isTabletScreen,
        // <XNG-Changes>
        isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
    };
};

const mapDispatchToProps = {
    xHeaderUpdate,
};

type Props = PageProps &
    HasShellContext &
    ThemeProps<BaseTheme> &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CompactHeaderStyles {
    page?: MobiusPageProps;
    mainNavigationWrapper?: InjectableCss;
    logoWrapper?: InjectableCss;
    searchWrapper?: InjectableCss;
    actionsWrapper?: InjectableCss;
    pageInnerWrapper?: InjectableCss;
    wrapper?: InjectableCss;
}

const boxShadowStyleMap: Dictionary<string> = {
    light: "rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px",
    medium: "rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px",
    dark: "rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
};

function generateShadow(props: ThemeProps<BaseTheme>) {
    const { isDropShadow, dropShadowStyle } = props.theme.header;
    if (isDropShadow && dropShadowStyle in boxShadowStyleMap) {
        return `box-shadow: ${boxShadowStyleMap[dropShadowStyle]}`;
    }
    return "";
}

function generateBorder(props: ThemeProps<BaseTheme>) {
    const { isBorder, borderColor, borderThickness, isBorderTop, isBorderLeft, isBorderBottom, isBorderRight } =
        props.theme.header;
    if (!isBorder) {
        return "border: none;";
    }

    let border = `border: ${borderThickness}px solid ${resolveColor(borderColor, props.theme)};`;

    if (!isBorderTop) {
        border += "border-top: none;";
    }
    if (!isBorderBottom) {
        border += "border-bottom: none;";
    }
    if (!isBorderLeft) {
        border += "border-left: none;";
    }
    if (!isBorderRight) {
        border += "border-right: none;";
    }

    return border;
}

// XNG-Changes: Style
export const compactHeaderStyles: CompactHeaderStyles = {
    page: {
        padding: 0, // * Padding doesn't work with fullWidth because of logic on Line 64 if Page component
        fullWidth: [true, true, true, true, true], // make the header full width and control the margins with container css
        css: css`
            position: fixed;
            top: 0;
            left: 0;
            z-index: ${getProp("theme.zIndex.compactHeader")};
            border-bottom: 1px solid ${color("common.ui5")};
            opacity: 0;
            transform: translateY(-100%) translateX(0);
            padding: ${props => props.theme.header.padding};
            background: ${color("common.ui1")};
            &&&& {
                position: fixed;
            }
        `,
    },
    logoWrapper: {
        css: css`
            display: flex;
            align-items: center;
            > *:first-child {
                margin-right: 36px;
            }
            > *:nth-child(2) {
                text-align: right;
            }
        `,
    },
    searchWrapper: {
        css: css`
            align-self: center;
            flex-grow: 1;
            max-width: 798px;
            margin: 0 36px;

            ${FormInputWrapper} ${InputStyle} {
                ${placeholderStyle}
            }
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            align-items: center;

            > div {
                margin-right: 32px;
            }
        `,
    },
    mainNavigationWrapper: {
        css: css`
            position: absolute;
            background: ${color("common.ui1")};
            width: 100%;
            top: -100px;
            transition: top 500ms ease-in-out;
            padding-top: 12px;
            padding-bottom: 12px;
            border-bottom: 1px solid ${color("common.ui5")};
            z-index: 0;
            ${({ showMainNavigation }: any) =>
                showMainNavigation &&
                css`
                    top: 100%;
                `}
            > div {
                ${layoutContainer}

                .container {
                    justify-content: left;
                    margin-left: 12px;
                }
            }
        `,
    },
    wrapper: {
        css: css`
            position: relative;
            background-color: ${color("common.ui1")};
            z-index: 3;
        `,
    },
};

const styles = compactHeaderStyles;

export const createStylesProviderStyles = (theme: BaseTheme) =>
    ({
        logo: {
            img: {
                css: css`
                    width: 135px;
                    height: 42px;
                `,
            },
        },
        headerSearchInput: {
            searchInputStyles: {
                popoverContentBody: {
                    as: "div",
                    _height: "auto",
                    css: css`
                        display: inline-flex;
                        justify-content: flex-end;
                        border-radius: 3px;
                    `,
                },
                searchHistoryStyles: {
                    wrapper: {
                        css: css`
                            display: flex;
                            flex-direction: column;
                            width: 240px;
                            padding: 0 20px 15px 20px;
                        `,
                    },
                },
            },
        },
    } as Styles);

const transitionStyles: Dictionary<{ opacity: number; transform: string }> = {
    entering: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    entered: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    exiting: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    exited: { opacity: 0, transform: "translateY(-100%) translateX(0)" },
};

const InnerWrapper = styled.div<{ isEdit?: boolean; isCheckoutPage?: boolean } & InjectableCss>`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    padding: ${props => (props.isEdit ? "30px 10px" : "12px 20px")};
    width: 100%;
    justify-content: space-between;
    margin: 0 auto;
    ${({ theme, isEdit, isCheckoutPage }) => {
        const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
        const checkoutStyles = isCheckoutPage ? "justify-content: space-between;" : "";
        return isEdit
            ? css`
                  max-width: 100%;
                  ${checkoutStyles}
              `
            : breakpointMediaQueries(
                  theme,
                  maxWidths.map((mw: any) =>
                      mw
                          ? css`
                                max-width: ${mw}px;
                                ${checkoutStyles}
                            `
                          : null,
                  ),
              );
    }}
    & #logo-and-search-wrapper {
        display: flex;
        flex-grow: 1;
    }
    ${injectCss}
`;

const CompactHeader = (props: Props) => {
    const { isCurrentPage, isEditing, isInShell } = props.shellContext;
    const isShell = isCurrentPage && isInShell;
    const isEdit = isCurrentPage && isEditing && isInShell;
    const [isShow, setIsShow] = useState(false);
    // <XNG-Changes>
    const [showMainNavigation, setShowMainNavigation] = useState(false);
    const headerWidth = typeof document !== "undefined" ? useElementSize(document.body)?.width : "100%";
    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);
    // </XNG-Changes>

    useEffect(() => {
        function eventHandler() {
            const scrollPos = Math.round(window.scrollY);
            // XNG-Changes
            // const windowWidth = Math.round(window.innerWidth);
            let isSticky = false;
            if (scrollPos > props.theme.header.triggerPoint) {
                setIsShow(true);
                isSticky = true;
            } else if (isShow && scrollPos < Number(props.theme.header.triggerPoint) + 150) {
                setIsShow(false);
                isSticky = false;
            }

            props.xHeaderUpdate({ key: "isSticky", value: isSticky });
        }
        window.addEventListener("scroll", eventHandler, { passive: true });

        return () => {
            window.removeEventListener("scroll", eventHandler);
        };
    }, [props.theme.header.triggerPoint, isShow]);

    useEffect(() => {
        setIsShow(Boolean(isShell));
        setShowMainNavigation(Boolean(isShell));
    }, [isShell]);

    // <XNG-Changes>
    const openNavigationDrawer = () => {
        setShowMainNavigation(prevState => !prevState);
    };

    if (isTabletScreen) {
        return null;
    }
    // </XNG-Changes>

    // XNG-Changes: structure
    return (
        <Transition in={isShow} timeout={0}>
            {state => (
                <Page
                    {...styles.page}
                    as="header"
                    background={props?.theme?.header?.backgroundColor}
                    // XNG-Changes
                    id="compact-header"
                    data-test-selector="compact-header"
                    className={xClassNames.append(isShow ? "shown-compact-header" : "hidden-compact-header")}
                    style={{
                        transition: "all 350ms ease",
                        pointerEvents: isShow ? "auto" : "none",
                        ...transitionStyles[state],
                        // XNG-Changes
                        maxWidth: headerWidth,
                    }}
                >
                    <StylesProvider styles={createStylesProviderStyles(props.theme)}>
                        <StyledWrapper {...styles.wrapper}>
                            <InnerWrapper
                                isEdit={isEdit}
                                isCheckoutPage={props.isCheckoutPage}
                                {...styles.pageInnerWrapper}
                            >
                                <StyledWrapper id="logo-and-search-wrapper">
                                    <StyledWrapper {...styles.logoWrapper}>
                                        {props.isTabletScreen && !props.isCheckoutPage
                                            ? createWidgetElement("Header/MainNavigation", {
                                                  fields: { ...props.mainNavigationWidget },
                                              })
                                            : null}
                                        {!props.isTabletScreen && !props.isCheckoutPage ? (
                                            <Clickable
                                                onClick={openNavigationDrawer}
                                                data-test-selector="expandMobileMenu"
                                                aria-label="main-navigation"
                                            >
                                                <Icon
                                                    src={showMainNavigation ? MenuFocused : MenuInactive}
                                                    size={30}
                                                    color={showMainNavigation ? "common.ui6" : "common.ui8"}
                                                    css={css`
                                                        &:hover {
                                                            color: ${color("common.ui13")};
                                                        }
                                                    `}
                                                />
                                            </Clickable>
                                        ) : null}
                                        {createWidgetElement("Basic/Logo", {
                                            fields: {
                                                mobileSpecificImage: props.logoImageWidget?.logoImage,
                                                logoImage: props.logoImageWidget?.logoImage,
                                            },
                                        })}
                                    </StyledWrapper>
                                    {!props.isCheckoutPage && (
                                        <StyledWrapper id="compact-search-wrapper" {...styles.searchWrapper}>
                                            {createWidgetElement("CompactHeader/Search", { fields: {} })}
                                        </StyledWrapper>
                                    )}
                                </StyledWrapper>

                                {!props.isCheckoutPage && (
                                    <StyledWrapper {...styles.actionsWrapper}>
                                        {createWidgetElement("Common/HeaderSignIn", {
                                            fields: {
                                                ...props.headerSignInWidget,
                                            },
                                        })}
                                        {createWidgetElement("Common/CartLink", {
                                            fields: {
                                                ...props.cartLinkWidget,
                                            },
                                        })}
                                    </StyledWrapper>
                                )}
                            </InnerWrapper>
                        </StyledWrapper>
                        <StyledWrapper
                            {...{ showMainNavigation }}
                            {...styles.mainNavigationWrapper}
                            id="sticky-header-container"
                        >
                            <XngZone contentId={props.id} zoneName="Main Naviagtion" hint="Zone for Main Navigation" />
                        </StyledWrapper>
                    </StylesProvider>
                </Page>
            )}
        </Transition>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(withIsInShell(CompactHeader))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export default pageModule;
